import { useState, useEffect, useRef } from 'react';
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Circle,
  InfoBox
} from '@react-google-maps/api';
import {
  Box,
  Typography,
  IconButton
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useThemeContext } from '../../context/ThemeContext';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const MapComponent = ({ lat, lng, address, eventTime, isUsingHomeLocation }) => {
    const theme = useTheme();
    const { mode } = useThemeContext();
    const primaryColor = theme.palette.primary.main;
    const [mapHeight, setMapHeight] = useState('400px');
    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: import.meta.env.VITE_GOOGLE_API_KEY,
    });
  
    // Set showInfoWindow to true by default
    const [showInfoWindow, setShowInfoWindow] = useState(true);
    const [positionKey, setPositionKey] = useState(0);
    const prevPositionRef = useRef(null);
    const [infoBoxDimensions, setInfoBoxDimensions] = useState({ width: 320 }); // Default approximation
    const infoBoxRef = useRef(null);

    const center = { lat: Number(lat), lng: Number(lng) };

    const containerStyle = {
        width: '800px',
        height: mapHeight,
        borderRadius: '15px'
    };

    // Adjust map height for responsiveness
    const updateMapHeight = () => {
        const width = window.innerWidth;
        setMapHeight(width < 800 ? '300px' : '400px');
    };
    const getPixelOffset = () => {
        // Calculate the center offset (half the width)
        const xOffset = -Math.floor(infoBoxDimensions.width / 2);
        return new window.google.maps.Size(xOffset, -180); // Keep your Y offset the same
    };

    useEffect(() => {
        updateMapHeight();
        window.addEventListener('resize', updateMapHeight);
        return () => window.removeEventListener('resize', updateMapHeight);
    }, []);

        // Add a useEffect to measure the InfoBox after it renders
    useEffect(() => {
        if (showInfoWindow && infoBoxRef.current) {
            // Use setTimeout to allow the DOM to fully render
            const timeoutId = setTimeout(() => {
                const width = infoBoxRef.current.offsetWidth;
                if (width !== infoBoxDimensions.width) {
                    setInfoBoxDimensions({ width });
                }
            }, 50);
                
            return () => clearTimeout(timeoutId);
        }
    }, [showInfoWindow, infoBoxDimensions.width]);

    // Check if lat/lng changed, force re-render of Marker/Circle/InfoBox if so
    useEffect(() => {
        if (
            prevPositionRef.current &&
            (prevPositionRef.current.lat !== Number(lat) ||
                prevPositionRef.current.lng !== Number(lng))
        ) {
            setPositionKey((prev) => prev + 1);
            // Make sure InfoBox shows when position changes
            setShowInfoWindow(true);
        }
        prevPositionRef.current = { lat: Number(lat), lng: Number(lng) };
        
    }, [lat, lng]);


    if (loadError) return <div>Error loading maps</div>;
    if (!isLoaded) return <div>Loading Maps...</div>;

    // Create a custom pin with an anchor near the bottom
    // const createCustomMarkerIcon = () => ({
    //     path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
    //     fillColor: primaryColor,
    //     fillOpacity: 1,
    //     strokeWeight: 2,
    //     strokeColor: '#FFFFFF',
    //     rotation: 0,
    //     scale: 1.8,
    //     anchor: new window.google.maps.Point(12, 22), // tip of pin
    // });

    const createCustomMarkerIcon = () => {
        if (isUsingHomeLocation) {
            // Home icon SVG path (simple house shape)
            return {
                path: 'M12 3L2 12h3v7h6v-5h2v5h6v-7h3L12 3z',
                fillColor: primaryColor,
                fillOpacity: 1,
                strokeWeight: 2,
                strokeColor: '#FFFFFF',
                scale: 1.8,
                anchor: new window.google.maps.Point(12, 22),
            };
        }
        // Default pin icon
        return {
            path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
            fillColor: primaryColor,
            fillOpacity: 1,
            strokeWeight: 2,
            strokeColor: '#FFFFFF',
            rotation: 0,
            scale: 1.8,
            anchor: new window.google.maps.Point(12, 22),
        };
    };

    // Light mode map style - clean, default-like style
    const lightModeStyles = [
        {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }],
        },
        {
            featureType: 'poi.medical',
            elementType: 'labels',
            stylers: [{ visibility: 'on' }],
        },
    ];

    // Dark mode map style - dark theme with blue/purple highlights
    const darkModeStyles = [
        { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
        { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
        { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
        {
            featureType: "administrative.locality",
            elementType: "labels.text.fill",
            stylers: [{ color: "#d59563" }],
        },
        {
            featureType: "poi",
            elementType: "labels.text.fill",
            stylers: [{ color: "#d59563" }],
        },
        {
            featureType: "poi.park",
            elementType: "geometry",
            stylers: [{ color: "#263c3f" }],
        },
        {
            featureType: "poi.park",
            elementType: "labels.text.fill",
            stylers: [{ color: "#6b9a76" }],
        },
        {
            featureType: "road",
            elementType: "geometry",
            stylers: [{ color: "#38414e" }],
        },
        {
            featureType: "road",
            elementType: "geometry.stroke",
            stylers: [{ color: "#212a37" }],
        },
        {
            featureType: "road",
            elementType: "labels.text.fill",
            stylers: [{ color: "#9ca5b3" }],
        },
        {
            featureType: "road.highway",
            elementType: "geometry",
            stylers: [{ color: "#746855" }],
        },
        {
            featureType: "road.highway",
            elementType: "geometry.stroke",
            stylers: [{ color: "#1f2835" }],
        },
        {
            featureType: "road.highway",
            elementType: "labels.text.fill",
            stylers: [{ color: "#f3d19c" }],
        },
        {
            featureType: "transit",
            elementType: "geometry",
            stylers: [{ color: "#2f3948" }],
        },
        {
            featureType: "transit.station",
            elementType: "labels.text.fill",
            stylers: [{ color: "#d59563" }],
        },
        {
            featureType: "water",
            elementType: "geometry",
            stylers: [{ color: "#17263c" }],
        },
        {
            featureType: "water",
            elementType: "labels.text.fill",
            stylers: [{ color: "#515c6d" }],
        },
        {
            featureType: "water",
            elementType: "labels.text.stroke",
            stylers: [{ color: "#17263c" }],
        },
    ];

    const mapOptions = {
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
        zoomControl: true,
        styles: mode === 'dark' ? darkModeStyles : lightModeStyles,
    };

    return (
        <GoogleMap
            mapContainerStyle={containerStyle}
            center={center}
            zoom={18}
            options={mapOptions}
        >
            <Marker
                key={`marker-${positionKey}`}
                position={center}
                animation={window.google.maps.Animation.DROP}
                icon={createCustomMarkerIcon()}
                onClick={() => setShowInfoWindow(!showInfoWindow)}
            />

            {showInfoWindow && (
                <InfoBox
                    key={`infobox-${positionKey}`}
                    position={center}
                    options={{
                        // Negative Y offset to move the bubble above the marker.
                        // Tweak this until your arrow is ~10px above the top of the marker.
                        pixelOffset: getPixelOffset(),
                        closeBoxURL: "",
                        enableEventPropagation: true,
                        boxStyle: {
                            borderRadius: '8px',
                            boxShadow: '0 2px 7px 1px rgba(0, 0, 0, 0.3)',
                            overflow: 'visible'
                        },
                    }}
                >
                    {/* Parent container for bubble + arrow */}
                    <Box ref={infoBoxRef} sx={{ position: 'relative', overflow: 'visible' }}>
                        {/* Bubble content */}
                        <Box
                            sx={{
                                borderRadius: '8px',
                                width: '320px',
                                bgcolor: theme.palette.background.paper,
                                // no "overflow: hidden" here, or it might clip the arrow
                            }}
                        >
                            {/* Header with title and close button */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: 1,
                                    bgcolor: primaryColor,
                                    color: 'white',
                                    borderTopLeftRadius: '8px',
                                    borderTopRightRadius: '8px',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <LocationOnIcon sx={{ mr: 1 }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                        Nearest Address
                                    </Typography>
                                </Box>

                                <IconButton
                                    size="small"
                                    sx={{ color: 'white', p: 0.5 }}
                                    onClick={() => setShowInfoWindow(false)}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>

                            {/* Body text */}
                            <Box sx={{ p: 1.5 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontSize: '14px',
                                        fontFamily: 'Roboto, Arial, sans-serif',
                                        lineHeight: 1.5,
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                     <span style={{ fontWeight: 'bold' }}>Location as of:</span> {eventTime || '—'}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        mt: 1,
                                        fontSize: '14px',
                                        fontFamily: 'Roboto, Arial, sans-serif',
                                        lineHeight: 1.5,
                                        color: theme.palette.text.primary,
                                        fontWeight: 500
                                    }}
                                >
                                    {address || 'Location address unavailable'}
                                </Typography>
                            </Box>
                        </Box>

                        {/* The arrow at the bottom center of the bubble */}
                        <Box
                            sx={{
                                position: 'absolute',
                                left: '50%',
                                bottom: '-12px',
                                transform: 'translateX(-50%)',
                                width: 0,
                                height: 0,
                                borderLeft: '12px solid transparent',
                                borderRight: '12px solid transparent',
                                borderTop: `12px solid ${theme.palette.background.paper}`,
                                zIndex: 100,
                            }}
                        />
                    </Box>
                </InfoBox>
            )}

            {/* Optional circle around the marker */}
            <Circle
                key={`circle-${positionKey}`}
                center={center}
                radius={50}
                options={{
                    strokeColor: primaryColor,
                    strokeOpacity: 0.1,
                    strokeWeight: 2,
                    fillColor: primaryColor,
                    fillOpacity: 0.25,
                }}
            />
        </GoogleMap>
    );
};

export default MapComponent;
